/**
 * ReviewsScene — Animated review cards for the "Processing Reviews" stage.
 */
import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BRAND } from '../../config'
import type { ReviewsSceneData } from '../../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function avatarColor(name: string): string {
  const palette = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
    '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return palette[Math.abs(hash) % palette.length]
}

function StarRow({ rating }: { rating: number }) {
  const full = Math.floor(rating)
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="w-3 h-3"
          fill={i < full ? '#f59e0b' : 'none'}
          stroke={i < full ? '#f59e0b' : '#d1d5db'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  )
}

function ratingBorderColor(rating: number): string {
  if (rating >= 4) return '#16a34a'
  if (rating <= 2) return '#ef4444'
  return '#d97706'
}

// ── Main component ─────────────────────────────────────────────────────────────

interface ReviewsSceneProps {
  data: ReviewsSceneData | null
}

export function ReviewsScene({ data }: ReviewsSceneProps) {
  const { t } = useTranslation('scan')
  const reviews = data?.reviews ?? []
  const totalCount = data?.total_count ?? 0
  const avgRating = data?.average_rating ?? 0

  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    if (reviews.length === 0) return
    setVisibleCount(0)
    const timer = setInterval(() => {
      setVisibleCount(prev => {
        if (prev >= reviews.length) {
          clearInterval(timer)
          return prev
        }
        return prev + 1
      })
    }, 500)
    return () => clearInterval(timer)
  }, [reviews.length])

  if (reviews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
        <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center text-2xl">⭐</div>
        <p className="text-sm font-medium">{t('scene.reviews.gathering')}</p>
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              className="w-2 h-2 rounded-full bg-gray-300"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ repeat: Infinity, delay: i * 0.2, duration: 0.7 }}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header: stats bar */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 mb-5 px-1"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-900">{avgRating.toFixed(1)}</span>
          <div className="flex flex-col">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5" fill={i < Math.round(avgRating) ? '#f59e0b' : 'none'} stroke={i < Math.round(avgRating) ? '#f59e0b' : '#d1d5db'} strokeWidth={1.5} />
              ))}
            </div>
            <span className="text-xs text-gray-400 mt-0.5">{totalCount.toLocaleString()} {t('scene.reviews.showing', { visible: visibleCount, total: reviews.length }).replace(/Showing.*of/, '').trim()}</span>
          </div>
        </div>
        <div className="flex-1 h-px bg-gray-100" />
        <motion.span
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-xs font-medium text-gray-500"
        >
          {t('scene.reviews.showing', { visible: visibleCount, total: reviews.length })}
        </motion.span>
      </motion.div>

      {/* Review cards */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        <AnimatePresence>
          {reviews.slice(0, visibleCount).map((review, i) => {
            const initial = review.author.trim().charAt(0).toUpperCase() || '?'
            const color = avatarColor(review.author)
            const borderColor = ratingBorderColor(review.rating)

            return (
              <motion.div
                key={`${review.author}-${i}`}
                initial={{ opacity: 0, x: 40, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex gap-3"
                style={{ borderLeftWidth: 3, borderLeftColor: borderColor }}
              >
                {/* Avatar */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                  style={{ background: color }}
                >
                  {initial}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900 truncate">{review.author}</span>
                    <StarRow rating={review.rating} />
                  </div>
                  {review.text ? (
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                      "{review.text}"
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 italic">{t('scene.reviews.noReview')}</p>
                  )}
                </div>

                {/* Rating badge */}
                <div
                  className="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                  style={{ background: borderColor }}
                >
                  {review.rating.toFixed(0)}
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>

        {/* Progress indicator */}
        {visibleCount < reviews.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2 px-4 py-2"
          >
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: BRAND.red }}
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ repeat: Infinity, delay: i * 0.18, duration: 0.7 }}
                />
              ))}
            </div>
            <span className="text-xs text-gray-400">{t('scene.reviews.loadingMore')}</span>
          </motion.div>
        )}
      </div>
    </div>
  )
}
